import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSyncAlt, faUser,
  faCalendarAlt, faClock, faSpinner, faCheckCircle,
  faExclamationTriangle, faSearch, faFilter, faFileCsv,
  faTimes, faSortAmountDown, faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
  }
};

const API_URL = `${API_CONFIG2.apiUrl}conge/api_conge_employe.php`;

const ManageEmployeeConges = ({ employeId }) => {
  // États principaux
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [limit, setLimit] = useState(10);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingConge, setEditingConge] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    employeId: employeId,
    type: 'Annuel',
    dateDebut: '',
    dateFin: '',
    motif: ''
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const searchTimeoutRef = useRef(null);

  const types = ['Annuel', 'Maladie', 'Exceptionnel', 'Maternité', 'Paternité', 'Sans solde'];
  const statuts = ['En attente', 'Approuvé', 'Refusé'];
  const limitOptions = [5, 10, 25, 50];

  // ------------------------------------------------------------
  // Récupération des congés avec filtres
  // ------------------------------------------------------------
  const fetchConges = useCallback(async (page = 1) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: 'fetch',
        employeId,
        page,
        limit,
        search: searchTerm,
        type: filterType,
        statut: filterStatut,
        unique: new Date().getTime()
      });
      const url = `${API_URL}?${params.toString()}`;
      const data = await fetchJSON(url, { signal: controller.signal });
      if (data.status === 'success') {
        setConges(data.data);
        setPagination(data.pagination);
      } else {
        throw new Error(data.message || 'Erreur de chargement');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [employeId, limit, searchTerm, filterType, filterStatut]);

  // Rafraîchissement forcé
  const refreshConges = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchConges(pagination.currentPage);
      setToast({ show: true, message: 'Données actualisées', variant: 'success' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    } catch (err) {
      // déjà géré
    } finally {
      setRefreshing(false);
    }
  }, [fetchConges, pagination.currentPage]);

  // Réinitialiser à la page 1 quand un filtre change
  useEffect(() => {
    fetchConges(1);
  }, [searchTerm, filterType, filterStatut, limit]); // eslint-disable-line

  // Chargement initial
  useEffect(() => {
    isMountedRef.current = true;
    fetchConges(1);
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []); // eslint-disable-line

  // Gestion de la recherche avec debounce
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      // le useEffect se déclenchera
    }, 300);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('');
    setFilterStatut('');
  };

  // ------------------------------------------------------------
  // CRUD
  // ------------------------------------------------------------
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const isEdit = !!editingConge;
    const action = isEdit ? 'update' : 'create';
    const method = isEdit ? 'PUT' : 'POST';
    const payload = { ...formData, employeId };

    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowModal(false);
        fetchConges(pagination.currentPage);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await fetchJSON(
        `${API_URL}?action=delete&id=${formData.id}&employeId=${employeId}`,
        { method: 'DELETE' }
      );
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowDeleteModal(false);
        fetchConges(pagination.currentPage);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setDeleting(false);
    }
  };

  // ------------------------------------------------------------
  // Export CSV
  // ------------------------------------------------------------
  const exportCSV = () => {
    if (!conges.length) {
      setToast({ show: true, message: 'Aucune donnée à exporter.', variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return;
    }
    const headers = ['ID', 'Type', 'Début', 'Fin', 'Jours', 'Statut', 'Motif'];
    const rows = conges.map(c => [
      c.id,
      c.type,
      c.dateDebut,
      c.dateFin,
      c.nbJours,
      c.statut,
      c.motif
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conges_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ------------------------------------------------------------
  // Gestion des modales
  // ------------------------------------------------------------
  const openCreateModal = () => {
    setEditingConge(null);
    setFormData({
      id: '',
      employeId: employeId,
      type: 'Annuel',
      dateDebut: '',
      dateFin: '',
      motif: ''
    });
    setShowModal(true);
  };

  const openEditModal = (conge) => {
    setEditingConge(conge);
    setFormData({
      id: conge.id,
      employeId: conge.employeId,
      type: conge.type,
      dateDebut: conge.dateDebut,
      dateFin: conge.dateFin,
      motif: conge.motif
    });
    setShowModal(true);
  };

  const openDeleteModal = (conge) => {
    setFormData(conge);
    setShowDeleteModal(true);
  };

  // ------------------------------------------------------------
  // Rendu
  // ------------------------------------------------------------
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #4f8cff;
          --accent-dark-blue: #2563eb;
          --accent-glow: rgba(79, 140, 255, 0.25);
          --bg-start: #0d1b2a;
          --bg-end: #0a0f1f;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(79, 140, 255, 0.15);
          --text-primary: #e8edff;
          --text-secondary: #a5b4d9;
          --text-muted: #5e6f94;
          --radius: 20px;
          --shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
        }
        * {
          box-sizing: border-box;
        }
        .monetra-page {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, #0a0f1f);
          min-height: 100vh;
          padding: clamp(0.8rem, 2vw, 2.5rem);
          color: var(--text-primary);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 2vw, 2rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
        }
        .glass-card:hover {
          border-color: rgba(79, 140, 255, 0.25);
          box-shadow: 0 16px 48px rgba(79, 140, 255, 0.05);
        }

        .monetra-btn {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.95rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.3);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(79, 140, 255, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.1);
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 2px 2px 2px 20px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.1);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .filter-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          color: var(--text-primary);
          padding: 8px 14px;
          outline: none;
          min-width: 120px;
          transition: 0.3s;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          min-height: 44px;
        }
        .filter-select:focus {
          border-color: var(--accent-blue);
        }
        .filter-select option {
          background: #0d1b2a;
          color: var(--text-primary);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
        }
        .glass-table th {
          padding: 14px 12px;
          text-align: left;
          font-size: clamp(0.65rem, 0.7vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 12px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.15s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .status-badge {
          padding: 4px 14px;
          border-radius: 30px;
          font-size: clamp(0.6rem, 0.7vw, 0.75rem);
          font-weight: 600;
          display: inline-block;
        }
        .status-badge.en-attente {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }
        .status-badge.approuvé {
          background: rgba(79, 140, 255, 0.15);
          color: var(--accent-blue);
        }
        .status-badge.refusé {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(79, 140, 255, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(79, 140, 255, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(79, 140, 255, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 10px 14px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.9rem, 1.1vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0d1b2a;
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.85) !important;
          backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(79, 140, 255, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .ms-auto { margin-left: auto; }

        @media (max-width: 768px) {
          .monetra-page { padding: 10px; }
          .glass-card { padding: 1rem; border-radius: 16px; }
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .glass-table th, .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .search-wrapper {
            min-width: unset;
            width: 100%;
            max-width: 100% !important;
          }
          .filter-select {
            width: 100%;
            min-width: unset;
          }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              <FontAwesomeIcon icon={faUser} className="me-2 text-secondary" />
              Mes congés
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              {pagination.totalRecords} demande(s) au total
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="monetra-btn" onClick={refreshConges} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={conges.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Export CSV
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal} disabled={loading}>
              <FontAwesomeIcon icon={faPlus} /> Nouveau
            </button>
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="glass-card mb-4 d-flex flex-wrap gap-3 align-items-center">
          <div className="search-wrapper" style={{ minWidth: '240px', flex: 2, maxWidth: '500px' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (type, motif, statut...)"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="btn"
                onClick={() => setSearchTerm('')}
                style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none', padding: '0 10px' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <FontAwesomeIcon icon={faFilter} className="text-secondary" />
            <select
              className="filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Tous types</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select
              className="filter-select"
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
            >
              <option value="">Tous statuts</option>
              {statuts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(filterType || filterStatut || searchTerm) && (
              <button className="monetra-btn outline" onClick={clearFilters}>
                <FontAwesomeIcon icon={faTimes} /> Effacer
              </button>
            )}
          </div>

          <div className="ms-auto d-flex align-items-center gap-2">
            <span className="text-muted small">Ligne(s)</span>
            <select
              className="filter-select"
              style={{ minWidth: '70px', padding: '4px 10px' }}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {limitOptions.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* Tableau */}
        <div className="glass-card p-0 overflow-hidden" style={{ position: 'relative' }}>
          {refreshing && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(11, 20, 40, 0.6)',
              backdropFilter: 'blur(3px)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius)'
            }}>
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="text-secondary mt-3">Mise à jour…</p>
            </div>
          )}

          <div className="table-responsive" style={{ opacity: refreshing ? 0.4 : 1, transition: 'opacity 0.25s' }}>
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Début</th>
                  <th>Fin</th>
                  <th>Jours</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" variant="primary" size="sm" /></td></tr>
                ) : conges.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-5 text-secondary">Aucune demande trouvée.</td></tr>
                ) : (
                  conges.map(c => (
                    <tr key={c.id}>
                      <td><span className="fw-bold text-secondary">#{c.id}</span></td>
                      <td><span className="text-secondary">{c.type}</span></td>
                      <td><span className="text-secondary"><FontAwesomeIcon icon={faCalendarAlt} className="me-1" />{c.dateDebut}</span></td>
                      <td><span className="text-secondary">{c.dateFin}</span></td>
                      <td><span className="text-secondary"><FontAwesomeIcon icon={faClock} className="me-1" />{c.nbJours}</span></td>
                      <td>
                        <span className={`status-badge ${c.statut.toLowerCase().replace(' ', '-')}`}>
                          {c.statut}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="monetra-btn me-1 p-2"
                          onClick={() => openEditModal(c)}
                          disabled={loading || refreshing}
                          title="Modifier"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="monetra-btn danger p-2"
                          onClick={() => openDeleteModal(c)}
                          disabled={loading || refreshing}
                          title="Supprimer"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">
              Page {pagination.currentPage} sur {pagination.totalPages || 1}
            </span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchConges(pagination.currentPage - 1)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchConges(pagination.currentPage + 1)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODALE AJOUT / MODIFICATION */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faEdit} className="text-success me-2" />
              {editingConge ? 'Modifier' : 'Ajouter'} une demande
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">Type</label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  className="monetra-input"
                  disabled={submitting}
                >
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Motif</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                  required
                  className="monetra-input text-danger"
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Date de début</label>
                <Form.Control
                  type="date"
                  value={formData.dateDebut}
                  onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                  required
                  className="monetra-input"
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Date de fin</label>
                <Form.Control
                  type="date"
                  value={formData.dateFin}
                  onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                  required
                  className="monetra-input"
                  disabled={submitting}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn" disabled={submitting}>
              Annuler
            </Button>
            <Button type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement…</> : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE SUPPRESSION */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer cette demande de congé ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn" disabled={deleting}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={deleting}>
            {deleting ? <><FontAwesomeIcon icon={faSpinner} spin /> Suppression…</> : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TOAST */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span>{toast.message}</span>
              <button
                className="ms-auto"
                onClick={() => setToast(prev => ({ ...prev, show: false }))}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageEmployeeConges;